import React, { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GetData from "../../../services/getData";
import logPatientChange from "../../../services/patientLogs";
import { buildPatientLogText } from "../../../services/patientLogMessages";
import BlockTitle from "../../../components/layout/blockTitle/BlockTitle";
import DateInput from "../../../components/ui/DateInput";
import AiVoiceDictationModal, {
  setFormValueByPath,
} from "../../../components/ui/AiVoiceDictationModal";
import "../patients.css";
import "./PatientRepeatVisit.css";

const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatDateForDisplay = (isoDate) => {
  if (!isoDate) return "";
  const [datePart] = String(isoDate).trim().split(/[ T]/);
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return String(isoDate);
  return `${day}.${month}.${year}`;
};

const emptyForm = () => ({
  exam_date: todayIso(),
  doctor: "",
  complaints: "",
  observation_dynamics: "",
  prescriptions: "",
  medications: "",
  sick_leave: "",
  preferential_prescriptions: "",
});

const parseDocData = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (_) {
      return {};
    }
  }
  return {};
};

const mapDocDataToFormData = (raw, defaults = {}) => {
  const data = parseDocData(raw);
  const base = emptyForm();
  return {
    ...base,
    ...defaults,
    exam_date: data.exam_date || defaults.exam_date || base.exam_date,
    doctor:
      data.doctor != null && String(data.doctor).trim() !== ""
        ? String(data.doctor)
        : defaults.doctor || base.doctor,
    complaints: data.complaints ?? "",
    observation_dynamics: data.observation_dynamics ?? "",
    prescriptions: data.prescriptions ?? "",
    medications: data.medications ?? "",
    sick_leave: data.sick_leave ?? "",
    preferential_prescriptions: data.preferential_prescriptions ?? "",
  };
};

const buildDocDataPayload = (form) => ({
  exam_date: form.exam_date ?? "",
  doctor: form.doctor ?? "",
  complaints: form.complaints ?? "",
  observation_dynamics: form.observation_dynamics ?? "",
  prescriptions: form.prescriptions ?? "",
  medications: form.medications ?? "",
  sick_leave: form.sick_leave ?? "",
  preferential_prescriptions: form.preferential_prescriptions ?? "",
});

const formatDoctorLabel = (doctor) => {
  if (!doctor) return "";
  if (typeof doctor === "string") return doctor.trim();
  const name = String(doctor.name ?? doctor?.name?.value ?? "").trim();
  const post = String(doctor.post ?? doctor?.post?.value ?? "").trim();
  if (name && post) return `${name}, ${post}`;
  return name || post || "";
};

const normalizeDoctorsList = (raw) => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const list = [];
  raw.forEach((item) => {
    const label = formatDoctorLabel(item);
    if (!label || seen.has(label)) return;
    seen.add(label);
    list.push({
      id: item?.id ?? item?.id?.value ?? label,
      label,
    });
  });
  return list;
};

const fetchCurrentDoctorLabel = async () => {
  try {
    const auth = await GetData({ module: "auth", block: "check" });
    const user = auth?.user;
    if (!user?.id) return user?.name || "";

    let post = "";
    try {
      const staffRes = await GetData({
        module: "staff",
        block: "info",
        id_staff: user.id,
      });
      let employee = staffRes?.result || {};
      if (Array.isArray(employee) && employee.length > 0) {
        employee = employee[0];
      }
      post = employee?.post?.value || employee?.type?.value || "";
    } catch (_) {
      /* должность необязательна */
    }

    const name = user.name || "";
    if (name && post) return `${name}, ${post}`;
    return name || post || "";
  } catch (_) {
    return "";
  }
};

function PatientRepeatVisit() {
  const { t } = useTranslation("patient");
  const navigate = useNavigate();
  const { patientId, docId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(emptyForm());
  const [savedFormData, setSavedFormData] = useState(emptyForm());
  const [doctors, setDoctors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [voiceUndoSnapshot, setVoiceUndoSnapshot] = useState(null);
  const shouldAutoPrint = searchParams.get("print") === "1";

  useEffect(() => {
    loadDocument();
  }, [patientId, docId]);

  const showAlert = (message) => {
    setModalMessage(message);
    setShowModal(true);
  };

  const loadDocument = async () => {
    setLoading(true);
    setError(null);
    try {
      const [docRes, doctorLabel, patientInfo] = await Promise.all([
        GetData({
          module: "patient",
          block: "patient_docs",
          action: "get",
          id_patient: patientId,
          id: docId,
        }),
        fetchCurrentDoctorLabel(),
        GetData({
          module: "patient",
          block: "info",
          id_patient: patientId,
        }),
      ]);

      if (docRes?.status === "fail") {
        throw new Error(docRes?.message || t("docs.errors.notFound"));
      }

      const doc = docRes?.result ?? null;
      if (!doc?.id) {
        throw new Error(t("docs.errors.notFound"));
      }
      if (Number(doc.template_id) !== 2) {
        throw new Error(t("prv.notRepeatVisit"));
      }

      const doctorsList = normalizeDoctorsList(
        patientInfo?.result?.all_doctors || []
      );
      setDoctors(doctorsList);

      const defaults = {
        exam_date: todayIso(),
        doctor: doctorLabel,
      };
      const mapped = mapDocDataToFormData(doc.data, defaults);
      setFormData(mapped);
      setSavedFormData(mapped);
    } catch (err) {
      setError(err.message || t("docs.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => setIsEditing(true);

  // ИИ-диктовка: перенос данных из диктовки врача в поля формы
  const handleVoiceOpen = () => setShowVoiceModal(true);

  const handleVoiceApply = (items) => {
    if (!Array.isArray(items) || items.length === 0) return;
    setVoiceUndoSnapshot(formData);
    setFormData((prev) =>
      items.reduce(
        (acc, item) => setFormValueByPath(acc, item.path, item.value),
        prev,
      ),
    );
  };

  const handleVoiceUndo = () => {
    if (voiceUndoSnapshot) {
      setFormData(voiceUndoSnapshot);
    }
    setVoiceUndoSnapshot(null);
  };

  const handleCancel = () => {
    setFormData(savedFormData);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const payload = buildDocDataPayload(formData);
    try {
      const result = await GetData({
        module: "patient",
        block: "patient_docs",
        action: "update",
        id_patient: patientId,
        id: docId,
        data: payload,
      });

      if (result?.status === "fail") {
        throw new Error(result?.message || t("docs.errors.saveFailed"));
      }

      const mapped = mapDocDataToFormData(result?.result?.data ?? payload);
      setFormData(mapped);
      setSavedFormData(mapped);
      setIsEditing(false);
      showAlert(t("docs.errors.saved"));
    } catch (err) {
      console.error("Ошибка сохранения:", err);
      showAlert(t("docs.errors.saveFailedWith", { message: err.message }));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("prv.confirmDelete"))) return;

    try {
      const result = await GetData({
        module: "patient",
        block: "patient_docs",
        action: "delete",
        id_patient: patientId,
        id: docId,
      });

      if (result?.status !== "success") {
        throw new Error(result?.message || t("docs.errors.deleteFailed"));
      }

      await logPatientChange({
        idPatient: patientId,
        idItem: docId,
        text: buildPatientLogText("patient_docs_deleted", {
          details: "Повторный прием",
        }),
      });

      navigate(`/patient/${patientId}/profile`);
    } catch (err) {
      console.error("Ошибка удаления:", err);
      showAlert(t("docs.errors.deleteFailedWith", { message: err.message }));
    }
  };

  const handlePrint = () => {
    if (typeof window === "undefined") return;
    const body = document.body;
    if (!body) {
      window.print();
      return;
    }

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      body.classList.remove("print-only-profile", "print-profile");
      window.removeEventListener("afterprint", cleanup);
    };

    window.addEventListener("afterprint", cleanup);
    body.classList.add("print-only-profile", "print-profile");
    window.print();
  };

  useEffect(() => {
    if (!shouldAutoPrint || loading || error) return;
    const timer = window.setTimeout(() => {
      handlePrint();
      navigate(`/patient/${patientId}/profile/repeat/${docId}`, {
        replace: true,
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [shouldAutoPrint, loading, error, patientId, docId, navigate]);

  if (loading) return <div className="loading">{t("state.loading", { ns: "common" })}</div>;
  if (error) return <div className="error">{t("error", { ns: "common", message: error })}</div>;

  const f = isEditing ? formData : savedFormData;
  const readOnly = !isEditing;
  const doctorOptions = (() => {
    const list = [...doctors];
    const current = String(f.doctor || "").trim();
    if (current && !list.some((d) => d.label === current)) {
      list.unshift({ id: `custom:${current}`, label: current });
    }
    return list;
  })();

  const renderDoctorSelect = (className) => (
    <Form.Select
      className={className}
      name="doctor"
      value={f.doctor || ""}
      onChange={handleChange}>
      <option value="">{t("docs.doctorPlaceholder")}</option>
      {doctorOptions.map((d) => (
        <option key={d.id} value={d.label}>
          {d.label}
        </option>
      ))}
    </Form.Select>
  );

  const renderTextCell = (name, rows, className = "") => {
    const linedClass = `prv-lined--${rows}`;
    return readOnly ? (
      <div className={`prv-cell-text ${linedClass} ${className}`.trim()}>
        {f[name] || ""}
      </div>
    ) : (
      <Form.Control
        as="textarea"
        className={`prv-cell-input ${linedClass} ${className}`.trim()}
        name={name}
        rows={rows}
        value={f[name] || ""}
        onChange={handleChange}
      />
    );
  };

  return (
    <>
      <div className="patient-name-title-sticky">
        <BlockTitle
          title={t("prv.title")}
          buttons={
            isEditing
              ? [
                  {
                    label: (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        }}>
                        <svg
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          width="20"
                          height="20"
                          style={{ fill: "currentColor" }}>
                          <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-2.08A7 7 0 0 0 19 12z" />
                        </svg>
                        {t("docs.buttons.dictate")}
                      </span>
                    ),
                    onClick: handleVoiceOpen,
                    type: "light icon",
                  },
                  {
                    label: t("docs.buttons.save"),
                    onClick: handleSave,
                    type: "normal",
                  },
                  {
                    label: t("docs.buttons.cancel"),
                    onClick: handleCancel,
                    type: "light",
                  },
                ]
              : [
                  {
                    label: (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        }}>
                        <svg
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          width="20"
                          height="20"
                          style={{ fill: "currentColor" }}>
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04a1.003 1.003 0 0 0 0-1.42L18.37 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z" />
                        </svg>
                        {t("docs.buttons.edit")}
                      </span>
                    ),
                    onClick: handleEdit,
                    type: "light icon",
                  },
                  {
                    label: (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        }}>
                        <svg
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          width="20"
                          height="20"
                          style={{
                            fill: "currentColor",
                            transform: "scale(1.2)",
                            transformOrigin: "center",
                          }}>
                          <path d="M19 8H5a3 3 0 0 0-3 3v4h4v4h12v-4h4v-4a3 3 0 0 0-3-3zm-3 9H8v-5h8v5zm3-7a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM18 3H6v4h12V3z" />
                        </svg>
                        {t("docs.buttons.print")}
                      </span>
                    ),
                    onClick: handlePrint,
                    type: "light icon",
                  },
                  {
                    label: t("docs.buttons.delete"),
                    onClick: handleDelete,
                    type: "danger",
                  },
                  {
                    label: (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        }}>
                        <svg
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          width="20"
                          height="20"
                          style={{ color: "currentColor" }}>
                          <path
                            d="M9 14L4 9l5-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M4 9h11a4 4 0 1 1 0 8H9"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {t("docs.buttons.backToList")}
                      </span>
                    ),
                    onClick: () => navigate(`/patient/${patientId}/profile`),
                    type: "normal icon",
                  },
                ]
          }
        />
      </div>

      <Form className={`prv-form${isEditing ? " prv-form--editing" : ""}`}>
        <table className="prv-sheet">
          <colgroup>
            <col style={{ width: "50%" }} />
            <col style={{ width: "50%" }} />
          </colgroup>
          <tbody>
            <tr>
              <td colSpan={2} className="prv-cell-date">
                <div className="prv-date-row">
                  <span>{t("docs.labels.date")}</span>
                  {readOnly ? (
                    <span className="prv-date-value">
                      {formatDateForDisplay(f.exam_date) ||
                        t("docs.labels.datePlaceholder")}
                    </span>
                  ) : (
                    <DateInput
                      className="form-control prv-date-input"
                      name="exam_date"
                      value={f.exam_date || ""}
                      onChange={handleChange}
                    />
                  )}
                </div>
              </td>
            </tr>

            <tr>
              <td colSpan={2} className="prv-cell-body">
                <div className="prv-label">{t("docs.labels.complaints")}</div>
                {renderTextCell("complaints", 2)}
              </td>
            </tr>

            <tr>
              <td colSpan={2} className="prv-cell-body prv-cell-body--tall">
                <div className="prv-label">{t("docs.labels.observationDynamics")}</div>
                {renderTextCell("observation_dynamics", 8)}
              </td>
            </tr>

          </tbody>
        </table>

        <div className="prv-appointments">
          <div className="prv-appointment">
            <div className="prv-label">
              {t("docs.labels.prescriptions")}
            </div>
            {renderTextCell("prescriptions", 4)}
          </div>

          <div className="prv-appointment">
            <div className="prv-label">
              {t("docs.labels.medications")}
            </div>
            {renderTextCell("medications", 4)}
          </div>

          <div className="prv-appointment">
            <div className="prv-label">
              {t("docs.labels.sickLeave")}
            </div>
            {renderTextCell("sick_leave", 3)}
          </div>

          <div className="prv-appointment">
            <div className="prv-label">{t("docs.labels.preferentialPrescriptions")}</div>
            {renderTextCell("preferential_prescriptions", 2)}
          </div>

          <div className="prv-appointment">
            <div className="prv-doctor-row">
              <span className="prv-doctor-label">{t("docs.labels.doctor")}</span>
              {readOnly ? (
                <span className="prv-doctor-value">{f.doctor || ""}</span>
              ) : (
                renderDoctorSelect("prv-doctor-input")
              )}
            </div>
          </div>
        </div>
      </Form>

      {/* Печатная форма */}
      <div className="prv-print">
        <table className="prv-print-sheet">
          <colgroup>
            <col style={{ width: "50%" }} />
            <col style={{ width: "50%" }} />
          </colgroup>
          <tbody>
            <tr>
              <td colSpan={2} className="prv-print-date">
                {t("docs.labels.date")}{" "}
                <span className="prv-print-underline">
                  {formatDateForDisplay(savedFormData.exam_date)}
                </span>
              </td>
            </tr>
            <tr>
              <td colSpan={2}>
                <div className="prv-print-label">{t("docs.labels.complaints")}</div>
                <div className="prv-print-lined prv-print-lined--3">
                  {savedFormData.complaints}
                </div>
              </td>
            </tr>
            <tr>
              <td colSpan={2}>
                <div className="prv-print-label">
                  {t("docs.labels.observationDynamics")}
                </div>
                <div className="prv-print-lined prv-print-lined--8">
                  {savedFormData.observation_dynamics}
                </div>
              </td>
            </tr>
            <tr>
              <th>
                <div className="prv-print-label">
                  {t("docs.labels.prescriptions")}
                </div>
              </th>
              <th>
                <div className="prv-print-label">
                  {t("docs.labels.medications")}
                </div>
              </th>
            </tr>
            <tr>
              <td>
                <div className="prv-print-lined prv-print-lined--4">
                  {savedFormData.prescriptions}
                </div>
              </td>
              <td>
                <div className="prv-print-lined prv-print-lined--4">
                  {savedFormData.medications}
                </div>
              </td>
            </tr>
            <tr>
              <th>
                <div className="prv-print-label">
                  {t("docs.labels.sickLeave")}
                </div>
              </th>
              <th>
                <div className="prv-print-label">{t("docs.labels.preferentialPrescriptions")}</div>
              </th>
            </tr>
            <tr>
              <td>
                <div className="prv-print-lined prv-print-lined--3">
                  {savedFormData.sick_leave}
                </div>
              </td>
              <td>
                <div className="prv-print-lined prv-print-lined--2">
                  {savedFormData.preferential_prescriptions}
                </div>
                <div className="prv-print-doctor">
                  <span>{t("docs.labels.doctor")}</span>
                  <span className="prv-print-underline">
                    {savedFormData.doctor}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Body>{modalMessage}</Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowModal(false)}>
            {t("actions.ok", { ns: "common" })}
          </button>
        </Modal.Footer>
      </Modal>

      <AiVoiceDictationModal
        show={showVoiceModal}
        onHide={() => setShowVoiceModal(false)}
        patientId={patientId}
        templateId={2}
        formData={formData}
        onApply={handleVoiceApply}
        onUndo={handleVoiceUndo}
      />
    </>
  );
}

export default PatientRepeatVisit;
